import React from "react";
import { useTranslation } from "react-i18next";
import { measureRenderedImageBox } from "../../canvas/image-measure";
import {
  EmailBuilderAlignmentControl,
  EmailBuilderBoundedRange,
  EmailBuilderImageWidthControl,
} from "../../shell/panel/blockSettings/EmailBuilderBlockSettingsShared";
import { EmailBuilderSettingsInlineField } from "../../shell/panel/blockSettings/EmailBuilderSettingsLayout";
import { EmailBuilderSettingsCornerRoundingSection } from "../controls/EmailBuilderSettingsCornerRoundingSection";
import { EmailBuilderSettingsPaddingSection } from "../controls/EmailBuilderSettingsPaddingSection";
import { ImageBlockMediaField } from "../controls/ImageBlockMediaField";
import { EmailBuilderSettingsImageOverlayFields } from "../controls/EmailBuilderSettingsImageOverlayFields";
import { SettingSection } from "../controls/SettingSection";
import { UrlField } from "../UrlField";
import {
  baselineImageBorderRadius,
  baselineImagePadding,
} from "../../domain/baselines";
import { blockContentMaxWidth, resolveBlockLayoutContext } from "../../domain/block-width";
import { findColumn, findRow } from "../../domain/find";
import {
  canUseFillColumn,
  defaultImageHeightPx,
  initialFixedImageHeightPx,
  IMAGE_HEIGHT_MAX,
  IMAGE_HEIGHT_MIN,
  IMAGE_OBJECT_FIT_OPTIONS,
} from "../../domain/image-height";
import { selectDocument } from "../../domain/selectors";
import { useEditorStore } from "../../domain/redux/hooks";
import { BLOCK_PADDING } from "../../domain/create-shared";
import type { ImageBlock, ImageHeightMode } from "../../domain/types";
import type { BlockSettingsProps } from "./types";
import {
  isCornerRoundingInactive,
  isPaddingInactive,
  surfaceCornerRoundingAddDefault,
} from "../settingsSectionActivation";

const IMAGE_HEIGHT_MODE_OPTIONS: {
  value: ImageHeightMode;
  label: string;
}[] = [
  { value: "auto", label: "Auto" },
  { value: "fillColumn", label: "Fill column" },
  { value: "fixed", label: "Custom" },
];

export function ImageBlockSettings({
  block,
  patch,
  rowId,
  columnId,
}: BlockSettingsProps & { block: ImageBlock }) {
  const { t } = useTranslation();
  const document = useEditorStore(selectDocument);
  const row = rowId ? findRow(document, rowId) : undefined;
  const column =
    rowId && columnId ? findColumn(document, rowId, columnId) : undefined;
  const fillColumnAllowed = canUseFillColumn(row, column, block);
  const defaultRadius = baselineImageBorderRadius();
  const layout =
    rowId && columnId
      ? resolveBlockLayoutContext(document, rowId, columnId, block.padding)
      : null;
  const contentMaxWidth = layout?.contentMaxWidth ?? 0;
  const widthSliderMax = layout
    ? blockContentMaxWidth(layout.rowInnerWidth, block.padding)
    : contentMaxWidth;
  const heightModeOptions = IMAGE_HEIGHT_MODE_OPTIONS.filter(
    option => option.value !== "fillColumn" || fillColumnAllowed,
  );

  function patchHeightMode(mode: ImageHeightMode) {
    if (mode === block.heightMode) {
      return;
    }
    if (mode === "auto") {
      patch({ heightMode: "auto", heightPx: null });
      return;
    }
    if (mode === "fillColumn") {
      patch({ heightMode: "fillColumn", heightPx: null });
      return;
    }
    const rendered = measureRenderedImageBox(block.id);
    patch({
      heightMode: "fixed",
      heightPx: initialFixedImageHeightPx(
        block,
        contentMaxWidth,
        rendered?.height,
        rendered?.naturalSize,
      ),
    });
  }

  return (
    <>
      <SettingSection sectionId="content" title={t("Content")}>
        <ImageBlockMediaField
          block={block}
          patch={patch}
          rowId={rowId}
          columnId={columnId}
        />
        <UrlField
          label={t("Link")}
          value={block.href}
          onChange={href => patch({ href })}
        />
      </SettingSection>

      <SettingSection sectionId="layout" title={t("Layout")}>
        <EmailBuilderImageWidthControl
          widthPx={block.widthPx ?? null}
          fullWidthMobile={block.fullWidthMobile}
          intrinsicSize={block.intrinsicSize}
          contentMaxWidth={contentMaxWidth}
          widthSliderMax={widthSliderMax}
          onChange={widthPatch => patch(widthPatch)}
        />
        <EmailBuilderSettingsInlineField label={t("Height")}>
          <div className={"email-builder-settings-segmented-field"}>
            <div
              className={"email-builder-settings-segment-wide"}
              role="group"
              aria-label={t("Height")}
            >
              {heightModeOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={block.heightMode === option.value}
                  data-on={
                    block.heightMode === option.value ? "true" : "false"
                  }
                  onClick={() => patchHeightMode(option.value)}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>
            {block.heightMode === "fixed" ? (
              <EmailBuilderBoundedRange
                label={t("Height (px)")}
                value={defaultImageHeightPx(block)}
                min={IMAGE_HEIGHT_MIN}
                max={IMAGE_HEIGHT_MAX}
                defaultValue={defaultImageHeightPx(block)}
                onChange={heightPx => {
                  if (heightPx === undefined) {
                    return;
                  }
                  patch({ heightPx });
                }}
              />
            ) : null}
          </div>
        </EmailBuilderSettingsInlineField>
        {block.heightMode !== "auto" ? (
          <EmailBuilderSettingsInlineField label={t("Fit")}>
            <div
              className={"email-builder-settings-segment-wide"}
              role="group"
              aria-label={t("Fit")}
            >
              {IMAGE_OBJECT_FIT_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  aria-pressed={block.objectFit === option.value}
                  data-on={block.objectFit === option.value ? "true" : "false"}
                  onClick={() => patch({ objectFit: option.value })}
                >
                  {t(option.label)}
                </button>
              ))}
            </div>
          </EmailBuilderSettingsInlineField>
        ) : null}
        <EmailBuilderAlignmentControl
          value={block.align}
          onChange={align => patch({ align })}
        />
      </SettingSection>

      <SettingSection
        sectionId="padding"
        title={t("Padding")}
        activation={{
          mode: "optional",
          isActive: !isPaddingInactive(block.padding),
          onAdd: () => patch({ padding: { ...BLOCK_PADDING } }),
        }}
      >
        <EmailBuilderSettingsPaddingSection
          fieldLabel={t("Image padding (px)")}
          value={block.padding}
          defaultValue={baselineImagePadding()}
          onChange={padding => {
            patch({ padding: { ...padding } });
          }}
        />
      </SettingSection>

      <SettingSection
        sectionId="corner-rounding"
        title={t("Corner rounding")}
        activation={{
          mode: "optional",
          isActive: !isCornerRoundingInactive(block.borderRadius),
          onAdd: () => patch({ borderRadius: surfaceCornerRoundingAddDefault() }),
        }}
      >
        <EmailBuilderSettingsCornerRoundingSection
          value={block.borderRadius ?? defaultRadius}
          onChange={borderRadius => {
            patch({
              borderRadius: {
                topLeft: borderRadius.topLeft,
                topRight: borderRadius.topRight,
                bottomRight: borderRadius.bottomRight,
                bottomLeft: borderRadius.bottomLeft,
              },
            });
          }}
        />
      </SettingSection>

      {block.media != null ? (
        <SettingSection sectionId="appearance" title={t("Appearance")}>
          <EmailBuilderSettingsImageOverlayFields
            value={block.imageOverlay}
            onChange={imageOverlay => patch({ imageOverlay })}
          />
        </SettingSection>
      ) : null}
    </>
  );
}
